import { fromEvent, interval, merge, of } from "rxjs";
import {
  mergeMap,
  groupBy,
  filter,
  distinctUntilKeyChanged,
  switchMap,
  map,
  distinctUntilChanged,
  sample,
} from "rxjs/operators";
import * as THREE from "three";
import { setHover } from "../../threeSlice";

export const InteractionSystem = (world, props = {}) => {
  const raycaster = new THREE.Raycaster();
  const clickableObject3Ds = [];
  const hoverableObject3Ds = [];

  const mouseEventToCoordinate = (mouseEvent) => {
    mouseEvent.preventDefault();
    return {
      x: mouseEvent.clientX,
      y: mouseEvent.clientY,
    };
  };

  const touchEventToCoordinate = (touchEvent) => {
    touchEvent.preventDefault();
    return {
      x: touchEvent.changedTouches[0].clientX,
      y: touchEvent.changedTouches[0].clientY,
    };
  };

  const touchEnd$ = fromEvent(world.canvas, "touchend").pipe(
    map(touchEventToCoordinate)
  );
  const mouseUp$ = fromEvent(world.canvas, "mouseup").pipe(
    map(mouseEventToCoordinate)
  );
  const mouseup$ = merge(touchEnd$, mouseUp$);

  mouseup$.subscribe((position) => {
    const { width, height } = world.canvas.getBoundingClientRect();
    raycaster.setFromCamera(
      {
        x: (position.x / width) * 2 - 1,
        y: -(position.y / height) * 2 + 1,
      },
      world.camera
    );
    const intersects = raycaster.intersectObjects(
      // filter hidden objects
      clickableObject3Ds.filter((object3D) => {
        if (!object3D) {
          return false;
        }
        let o = object3D;
        while (o && o.parent) {
          if (!o.visible) {
            return false;
          }
          o = o.parent;
        }
        return true;
      })
    );
    if (
      intersects.length > 0 &&
      intersects[0] &&
      intersects[0].object &&
      intersects[0].object.userData
    ) {
      const { interactionId } = intersects[0].object.userData;

      const entity = world.entityManager.getEntity(interactionId);

      if (entity && entity.interaction && entity.interaction.clickAction) {
        world.store.dispatch(entity.interaction.clickAction);
      }
    }
  });

  const hover$ = fromEvent(world.canvas, "mousemove").pipe(
    sample(interval(50)), // limit events
    map((position) => {
      const { width, height } = world.canvas.getBoundingClientRect();
      raycaster.setFromCamera(
        {
          x: (position.x / width) * 2 - 1,
          y: -(position.y / height) * 2 + 1,
        },
        world.camera
      );

      const intersects = raycaster.intersectObjects(
        hoverableObject3Ds.filter((object3D) => {
          if (!object3D) {
            return false;
          }
          let o = object3D;
          while (o && o.parent) {
            if (!o.visible) {
              return false;
            }
            o = o.parent;
          }
          return true;
        })
      );

      if (
        intersects.length > 0 &&
        intersects[0] &&
        intersects[0].object &&
        intersects[0].object.userData
      ) {
        const { interactionId } = intersects[0].object.userData;
        const entity = world.entityManager.getEntity(interactionId);
        return { entity, position };
      }
      return null;
    }),
    distinctUntilChanged(
      (prev, curr) =>
        prev === curr ||
        (prev &&
          curr &&
          prev.entity &&
          curr.entity &&
          prev.entity.id === curr.entity.id)
    )
  );

  hover$.subscribe((result) => {
    world.store.dispatch(setHover(result));
  });

  world.data.entity$
    .pipe(
      groupBy(
        (entity) => entity.id,
        (entity) => entity
      ),

      mergeMap((group$) =>
        group$.pipe(
          filter((entity) => entity.hasOwnProperty("object3D")),
          filter((entity) => entity.hasOwnProperty("interaction")),

          // TOOD: combine with: distinctUntilKeyChanged("object3D"),
          distinctUntilKeyChanged("interaction"),

          // insert object3D
          switchMap((definition) => {
            if (definition.object3D) {
              return world.object3DManager
                .getObject3D$(definition.object3D)
                .pipe(map((object3D) => ({ definition, object3D })));
            } else {
              return of({ definition });
            }
          })
        )
      )
    )

    .subscribe(({ definition, object3D }) => {
      if (!object3D) {
        return;
      }

      if (definition.interaction.clickable) {
        // remove old definitions
        const index = clickableObject3Ds.findIndex(
          (item) => item.userData.interactionId === definition.id
        );

        if (index !== -1) {
          clickableObject3Ds.splice(index, 1);
        }
        clickableObject3Ds.push(object3D);
      }

      if (definition.interaction.hoverable) {
        // remove old definitions
        const index = hoverableObject3Ds.findIndex(
          (item) => item.userData.interactionId === definition.id
        );

        if (index !== -1) {
          hoverableObject3Ds.splice(index, 1);
        }
        hoverableObject3Ds.push(object3D);
      }

      object3D.userData = {
        ...object3D.userData,
        interactionId: definition.id,
      };
    });
};

export default InteractionSystem;
